import {useDispatch, useSelector} from "react-redux";
import {getHistoryArray, getIsHistoryFullyLoaded, getLatestDateWithChange} from "../../../ducks/history-duck.js";
import {getAssetIds} from "../../../ducks/assets-duck.js";
import {
    getChartDuration,
    getChartFocus,
    setChartHoverDate,
    setDiffTrackerValues,
    setIsDiffTrackerVisible
} from "../../../ducks/chart-duck.js";
import {useState} from "react";
import {trimArrayEvenly} from "../../../util/misc.js";
import {getDaysSinceStartOfYear} from "../../../util/time-and-date";

const CHART_VALUES_MAX_LENGTH = 999

const getChartValuesAggregate = (history, assetIds) => {
    const values = []
    const gains = []
    let maxValue = 0
    let maxGain = 0
    for (let i = 0; i < history.length; i++) {
        let gain = 0
        let value = 0
        for (const assetId of assetIds) {
            const asset = history[i][1][assetId]
            value += asset.value
            gain += asset.gain
        }
        // ignore zero value days at start of dataset
        if (!!value || values.length > 0) {
            maxValue = Math.max(value, maxValue)
            values.push(value)
        }
        if (!!gain || gains.length > 0) {
            maxGain = Math.max(gain, maxGain)
            gains.push(gain)
        }
    }
    return {
        value: {
            values: values,
            max: maxValue,
        },
        gain: {
            values: gains,
            max: maxGain,
        },
    }
}

const getStatistics = (valuesFull, maxValue) => {
    const latestValue = valuesFull[valuesFull.length - 1]
    let isLatestMovePositive

    const isATH = latestValue >= maxValue * .995
    for (let i = valuesFull.length - 2; i > 0; i--) {
        const value = valuesFull[i]
        if (value !== latestValue) {
            isLatestMovePositive = value < latestValue
            i = 0
        }
    }
    return {
        isATH,
        isLatestMovePositive,
    }
}

export const useChartData = () => {

    const dispatch = useDispatch()

    const history = useSelector(getHistoryArray)
    const isHistoryFullyLoaded = useSelector(getIsHistoryFullyLoaded)
    const assetIds = useSelector(getAssetIds)

    const chartDuration = useSelector(getChartDuration)
    const chartFocus = useSelector(getChartFocus)
    const latestDateWithChange = useSelector(getLatestDateWithChange)

    const [isHovering, setIsHovering] = useState(false)

    const getPropFromDate = (prop) => (date) => assetIds.reduce((acc, cur) => acc + (date && date[1][cur] && date[1][cur][prop]), 0)

    const chartValuesAggregate = getChartValuesAggregate(history, assetIds)
    const {values: valuesFull, max: maxValue} = chartValuesAggregate[chartFocus]
    const {isLatestMovePositive, isATH} = getStatistics(valuesFull, maxValue)

    // ignore zero value days at start of dataset
    const historyZeroDaySlice = history.slice(history.length - valuesFull.length)

    const effectiveDuration = chartDuration === 'ytd'
        ? getDaysSinceStartOfYear()
        : chartDuration
    const sliceKey = (effectiveDuration + 1) * -1

    const historySlicePlusOne = trimArrayEvenly(historyZeroDaySlice.slice(sliceKey), CHART_VALUES_MAX_LENGTH)
    const historySlice = historySlicePlusOne.slice(sliceKey + 1)

    const labels = historySlice.map(date => date[0]).reverse()
    const values = trimArrayEvenly(valuesFull.slice(sliceKey + 1).reverse(), CHART_VALUES_MAX_LENGTH)

    const onMouseMove = dataIndex => {
        if (isHovering) {
            setHoverValuesFromDataIndex(dataIndex)
        }
    }

    const setHoverValuesFromDataIndex = (dataIndex, isMouseOut = false) => {
        const hoverDate = labels[dataIndex]
        const hoverValue = historySlice[historySlice.length - dataIndex - 1]
        const hoverValuePrev = historySlicePlusOne[historySlicePlusOne.length - dataIndex - 2]
        const value = getPropFromDate('value')(hoverValue)
        const valuePrev = getPropFromDate('value')(hoverValuePrev)
        const gain = getPropFromDate('gain')(hoverValue)
        const gainPrev = getPropFromDate('gain')(hoverValuePrev)
        dispatch(setChartHoverDate(hoverDate))
        if (!isMouseOut) {
            dispatch(setDiffTrackerValues({
                value,
                valuePrev,
                gain,
                gainPrev,
                date: hoverDate,
                relativePosition: dataIndex / labels.length,
            }))
        }
    }

    const onMouseOut = () => {
        const indexOf = labels.indexOf(latestDateWithChange)
        setHoverValuesFromDataIndex(indexOf, true)
        setIsHovering(false)
        dispatch(setIsDiffTrackerVisible(false))
    }

    const onMouseOver = () => {
        setIsHovering(true)
    }


    return {
        isATH,
        isLatestMovePositive,
        isHistoryFullyLoaded,
        values: values,
        labels: labels,
        isHovering,
        onMouseMove,
        onMouseOver,
        onMouseOut,
    }

}